import { Button, Form, Modal, Select, Steps } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  doGetDeliveryAreaLocations,
  doOverwriteDeliveryAreaLocations,
} from '../../modules/delivery-area-locations/stores/thunk';
import { useSelector } from 'react-redux';
import { selectAllDeliveryAreaLocations } from '../../modules/delivery-area-locations/stores/slice';
import { DeliveryAreaPrio } from '../Inputs/DeliveryAreaPrio';

export const DeliveryAreaModal = ({ open, cancel, done, location, deliveryAreas }) => {
  const dispatch = useDispatch();

  const deliveryAreaLocations = useSelector((state) => selectAllDeliveryAreaLocations(state));

  const [selectedDeliveryAreas, setSelectedDeliveryAreas] = useState([]);
  const [prioritizedDeliveryAreas, setPrioritizedDeliveryAreas] = useState([]);
  const [form] = Form.useForm();

  useEffect(() => {
    if (location?.id) {
      dispatch(doGetDeliveryAreaLocations({ locationId: location.id }));
    }
  }, [location]);

  // (re)set form state after fetching data
  useEffect(() => {
    setCurrent(0);
    setSelectedDeliveryAreas(deliveryAreaLocations.map((d) => d.deliveryAreaId));
    form.setFieldValue(
      'deliveryAreas',
      deliveryAreaLocations.map((d) => d.deliveryAreaId),
    );
  }, [deliveryAreaLocations]);

  const save = async () => {
    setCurrent(0);

    const update = prioritizedDeliveryAreas.map((p, index) => {
      const existing = deliveryAreaLocations.find((d) => d.deliveryAreaId === p.id);
      const withId = existing ? { id: existing.id } : {};

      return {
        ...withId,
        locationId: location.id,
        deliveryAreaId: p.id,
        priority: index + 1, // zero index plus 1
      };
    });

    // save update or create
    await dispatch(doOverwriteDeliveryAreaLocations({ locationId: location.id, update }));
    // refetch
    await dispatch(doGetDeliveryAreaLocations({ locationId: location.id }));
    done();
  };
  const steps = [
    {
      title: 'Select delivery areas',
    },
    {
      title: 'Prioritize delivery areas',
    },
  ];
  const [current, setCurrent] = useState(0);

  const next = () => {
    if (selectedDeliveryAreas.length > 0) {
      setCurrent(current + 1);
    } else {
      save();
    }
  };

  const prev = () => {
    setCurrent(current - 1);
  };
  const getAreaName = (area) => {
    return area.name;
  };

  const byOriginalPrio = (a, b) => {
    const originalA = deliveryAreaLocations.find((d) => d.deliveryArea.id === a.id)?.priority || Infinity;
    const originalB = deliveryAreaLocations.find((d) => d.deliveryArea.id === b.id)?.priority || Infinity;
    return originalA < originalB ? -1 : 1;
  };
  const filteredDeliveryAreas = useMemo(
    () =>
      deliveryAreas
        .filter((d) => selectedDeliveryAreas.some((s) => s === d.id))
        .map((d) => ({ ...d, enhancedName: getAreaName(d) }))
        .sort(byOriginalPrio),
    [selectedDeliveryAreas],
  );

  const cancelAndClear = () => {
    setSelectedDeliveryAreas([]);
    setPrioritizedDeliveryAreas([]);
    setCurrent(0);
    cancel();
  };
  const [disabled, setDisabled] = useState(true);

  return (
    <Modal width={600} title={`Configure delivery areas for location ${location?.name}`} open={open} closable={false}>
      <Steps current={current} items={steps} style={{ marginBottom: 16 }} />
      <div>
        {current === 0 ? (
          <Form layout="vertical" form={form}>
            <Form.Item
              label="Delivery Areas"
              name="deliveryAreas"
              style={{ marginRight: '15%' }} // to keep the next button visible
              initialValue={selectedDeliveryAreas}
            >
              <Select
                mode="multiple"
                size="large"
                allowClear
                options={deliveryAreas?.map((area) => ({
                  label: getAreaName(area),
                  value: area.id,
                }))}
                onChange={(v) => {
                  setSelectedDeliveryAreas(v);
                  setDisabled(false);
                }}
              />
            </Form.Item>
          </Form>
        ) : (
          <DeliveryAreaPrio deliveryAreas={filteredDeliveryAreas} setPrio={setPrioritizedDeliveryAreas} />
        )}
      </div>

      <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
        {current > 0 ? (
          <Button style={{ margin: '0 8px' }} onClick={() => prev()}>
            Previous
          </Button>
        ) : (
          <div></div> // for outlining
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="default" onClick={cancelAndClear}>
            Cancel
          </Button>
          {current < steps.length - 1 && (
            <>
              <Button type="primary" onClick={() => next()} disabled={disabled}>
                {selectedDeliveryAreas.length > 0 ? 'Next' : 'Save'}
              </Button>
            </>
          )}
          {current === steps.length - 1 && (
            <>
              <Button type="primary" onClick={save}>
                Save
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};
